import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
import { useAnalytics } from "@/lib/analytics";
import { useAuth } from "@/components/auth/AuthProvider";
import GradientLine from "@/components/ui/gradient-line";
import { useState, useEffect } from "react";
import { useTranslation } from "@/lib/i18n";

const Hero = () => {
  const { track } = useAnalytics();
  const { openAuth } = useAuth();
  const { translations: t } = useTranslation();
  const [wordIndex, setWordIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  const rotatingWords = t.hero.rotatingWords;

  useEffect(() => {
    const interval = setInterval(() => {
      setIsAnimating(true);
      setTimeout(() => {
        setWordIndex((prev) => (prev + 1) % rotatingWords.length);
        setIsAnimating(false);
      }, 300);
    }, 3000);
    return () => clearInterval(interval);
  }, [rotatingWords.length]);

  return (
    <section className="relative overflow-hidden bg-white dark:bg-black transition-colors duration-300 lg:min-h-[80vh] lg:flex lg:items-center">
      {/* Gradient Blobs Background */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-20 -right-32 w-80 h-80 bg-gradient-to-r from-purple-400/15 to-pink-400/15 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute top-1/3 -left-40 w-96 h-96 bg-gradient-to-r from-blue-400/10 to-cyan-400/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute bottom-20 right-20 w-64 h-64 bg-gradient-to-r from-yellow-400/12 to-orange-400/12 rounded-full blur-2xl animate-pulse delay-2000"></div>
        <div className="absolute top-1/2 left-1/4 w-48 h-48 bg-gradient-to-r from-green-400/8 to-emerald-400/8 rounded-full blur-3xl animate-pulse delay-500"></div>
        <div className="absolute bottom-1/4 left-1/3 w-72 h-72 bg-gradient-to-r from-indigo-400/10 to-purple-400/10 rounded-full blur-3xl animate-pulse delay-1500"></div>
      </div>

      {/* Dotted Background */}
      <div className="absolute inset-0 bg-[radial-gradient(circle,#80808030_1px,transparent_1px)] bg-[size:16px_16px]"></div>

      {/* Center gradient for readability */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,white_0%,white_20%,transparent_60%)] dark:bg-[radial-gradient(ellipse_at_center,black_0%,black_20%,transparent_60%)]"></div>

      {/* Content */}
      <div className="relative w-full px-6 sm:px-10 lg:px-20 xl:px-28 pt-24 sm:pt-32 pb-10 sm:pb-14">
        <div className="flex flex-col lg:flex-row items-center lg:items-center gap-12 lg:gap-20">
          {/* Left - Text Content */}
          <div className="flex-1 min-w-0 text-center lg:text-left">
            {/* Meta approved icon */}
            <div className="flex justify-center lg:justify-start mb-0">
              <img
                src="/metaapproved.svg"
                alt="Meta Approved"
                className="w-20 h-20 lg:w-28 lg:h-28 opacity-80 dark:hidden"
                loading="eager"
              />
              <img
                src="/svgviewer-output.svg"
                alt="Meta Approved"
                className="w-20 h-20 lg:w-28 lg:h-28 opacity-80 hidden dark:block"
                loading="eager"
              />
            </div>

            {/* Headline */}
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-neutral-900 dark:text-white leading-[1.1] tracking-tight mb-0">
              {t.hero.headline}
            </h1>
            <div className="overflow-hidden h-[1.2em] text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold">
              <span
                className={`inline-block text-brand-accent transition-all duration-500 ease-in-out ${isAnimating ? "-translate-y-full opacity-0" : "translate-y-0 opacity-100"}`}
              >
                {rotatingWords[wordIndex]}
              </span>
            </div>

            <GradientLine className="justify-center lg:justify-start sm:-mt-4 mb-2 -mt-2 [&>div]:w-48 [&>div]:sm:w-96 [&>div>div]:opacity-25 [&_svg]:opacity-60" />

            {/* Subheadline */}
            <p className="text-sm sm:text-base md:text-xl text-[#64657E] dark:text-gray-400 font-medium mb-8 sm:mb-10 max-w-lg mx-auto lg:mx-0 leading-relaxed">
              {t.hero.subheadline}
            </p>

            {/* CTA */}
            <div className="mb-4 flex flex-col items-center lg:items-start">
              <Button
                variant="gradient-brand"
                className="sm:w-auto w-full uppercase font-semibold px-8 py-5 sm:px-10 sm:py-6 rounded-2xl text-base group"
                onClick={(e) => {
                  e.preventDefault();
                  track("cta_clicked", {
                    cta_id: "CTA_Hero",
                    button_text: "Start For Free",
                  });
                  openAuth();
                }}
              >
                {t.hero.cta}
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>

            {/* Helper text */}
            <p className="text-sm text-neutral-500 dark:text-neutral-400 font-medium mb-10 sm:mb-12 text-center lg:text-left">
              {t.hero.helperText} &bull; {t.hero.helperSeparator}
            </p>

            {/* Trust row */}
            <div className="flex flex-row items-center justify-center lg:justify-start gap-4 sm:gap-6">
              {/* Avatar stack + trusted text */}
              <div className="flex items-center gap-3">
                <div className="flex -space-x-2">
                  {[
                    "https://cms.zapify.pro/uploads/squser1_b38e5a07df.avif",
                    "https://cms.zapify.pro/uploads/squser2_d16f9fa8fc.avif",
                    "https://cms.zapify.pro/uploads/squser3_de6595da8d.avif",
                    "https://cms.zapify.pro/uploads/squser4_5a6a55bc25.avif",
                  ].map((src, i) => (
                    <img
                      key={i}
                      src={src}
                      alt=""
                      width={32}
                      height={32}
                      className="w-7 h-7 sm:w-8 sm:h-8 rounded-full border-2 border-white dark:border-black object-cover"
                      loading="eager"
                    />
                  ))}
                </div>
                <div className="text-left">
                  <span className="text-xs text-neutral-500 dark:text-neutral-400 leading-none">
                    {t.hero.trustedBy}
                  </span>
                  <br />
                  <span className="text-sm font-semibold text-neutral-900 dark:text-white leading-none">
                    {t.hero.creators}
                  </span>
                </div>
              </div>

              {/* Divider */}
              <div className="w-px h-6 bg-neutral-300 dark:bg-neutral-700"></div>

              {/* DMs Sent stat */}
              <div className="text-left">
                <span className="text-xs text-neutral-500 dark:text-neutral-400 leading-none">
                  {t.hero.dmsSent}
                </span>
                <br />
                <span className="text-sm font-semibold text-neutral-900 dark:text-white leading-none">
                  {t.hero.dmsCount}
                </span>
              </div>
            </div>
          </div>

          {/* Right - Product Visual */}
          <div className="lg:w-[40%] flex-shrink-0 min-w-0 w-full flex justify-center lg:justify-end">
            <img
              src="/onboarding.gif"
              alt="Zapify automation demo showing Instagram comment replies and DM automation"
              className="w-full max-w-md sm:max-w-lg lg:max-w-full h-auto"
              loading="eager"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
